import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { BindingSignaler } from "aurelia-templating-resources";
import * as moment from "moment";
import { DialogServiceConstants } from "../../../../../../constants/basics/dialog.constants";
import { GenericConfirmationDialogModel } from "../../../../../../models/basics/generic-confirmation-dialog-model";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { RightShipInspectionModel, RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
@transient()
export class PortAndOperationalDetailsTab {

    isEdit: boolean;
    _resettingMaxDate: boolean = false;
    item: RightShipInspectionModel;
    portsMustHaveAtoBViaC: boolean = false;
    verifiedPortsOnly: boolean = true;

    private _isHybrid: boolean = null;

    activate(item: RightShipInspectionModel) {
        this.item = item;
    }

    constructor(private dialogClientService: DialogClientService, private bindingSignaler: BindingSignaler) { }

    countryChanged(event: { item: LookupValue, oldItem: LookupValue }) {
        if (event && event.item && Object.keys(event.item.additionalData).indexOf('isSanctioned') > -1 && event.item.additionalData['isSanctioned'] == true) {
            const model = new GenericConfirmationDialogModel();
            model.confirm = "Ok";
            model.messages.push('Country is sanctioned and cannot be selected');
            model.title = 'Sanctioned Country';
            model.onConfirm = () => {
                this.item.portId = null;
                this.item.countryId = null;
            };
            this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
        }

        if (event && event.item && event.item.name == "Unknown Country") {
            const model = new GenericConfirmationDialogModel();
            model.confirm = "Ok";
            model.messages.push('You cannot submit a RightShip Inspection Request with an Unknown Country');
            model.title = 'Unknown Country';
            model.onConfirm = () => {
                this.item.portId = null;
                this.item.countryId = null;
            };
            this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
        }

        if (event && event.item && Object.keys(event.item.additionalData).indexOf('isExcludedFromInspections') > -1 && event.item.additionalData['isExcludedFromInspections'] == true) {
            const model = new GenericConfirmationDialogModel();
            model.confirm = "Ok";
            model.messages.push('The selected country is currently restricted for travel. Please choose a different country');
            model.title = 'Restricted Country';
            model.onConfirm = () => {
                this.item.portId = null;
                this.item.countryId = null;
            };
            this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
        }
    }

    today() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    estArrivalMinDate() {
        if (!this.isCargill) {
            return this.item && this.item.estimatedArrivalDate && moment(this.item.estimatedArrivalDate).isBefore(moment(), 'd') ?
                new Date(this.item.estimatedArrivalDate) : this.today();
        }
        else return null;
    }

    @computedFrom('item.lengthOfTimeAtBerth')
    get lengthOfTimeAtBerthDays() {
        return this.item ? Math.floor(this.item.lengthOfTimeAtBerth) : null;
    }

    set lengthOfTimeAtBerthDays(x: number) {
        this.item.lengthOfTimeAtBerth = x + Number((this.lengthOfTimeAtBerthHours / 24).toFixed(5));
    }

    @computedFrom('item.lengthOfTimeAtBerth')
    get lengthOfTimeAtBerthHours() {
        const val = this.item ? Math.round((this.item.lengthOfTimeAtBerth - this.lengthOfTimeAtBerthDays) * 24) : null;
        return val;
    }

    @computedFrom('item.lengthOfTimeAtBerth')
    get lengthOfTimeAtBerthTotalHours() {
        const val = this.item && this.item.lengthOfTimeAtBerth ? this.item.lengthOfTimeAtBerth * 24: 0;
        return Math.ceil(val);
    }

    @computedFrom('item.estimatedArrivalDate', 'item.lengthOfTimeAtBerth', '_resettingMaxDate')
    get maxRequestDate() {
        if (this._resettingMaxDate) {
            setTimeout(() => {
                this._resettingMaxDate = false;
            }, 150);
            return null;
        }

        if (this.item && this.item.estimatedArrivalDate && this.item.lengthOfTimeAtBerth) {
            const maxDate = moment(this.item.estimatedArrivalDate).add(this.lengthOfTimeAtBerthDays + (this.lengthOfTimeAtBerthHours > 0 ? 1 : 0) - 1, "days");

            return maxDate.toDate().setHours(23, 59, 59);
        }
        else return null;
    }

    set lengthOfTimeAtBerthHours(x: number) {
        const val = this.lengthOfTimeAtBerthDays + Number((x / 24).toFixed(5));
        this.item.lengthOfTimeAtBerth = val;
        setTimeout(() => {
            this.bindingSignaler.signal('update');
        }, 200);
    }

    @computedFrom(Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.item),
    Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.item.rightShipInspectionTypeId))
    get isWet() {
        return this.item && this.item.rightShipInspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom(Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.item),
    Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.item.rightShipInspectionTypeId))
    get isCargill() {
        return this.item && this.item.rightShipInspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    @computedFrom(Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.isWet),
    Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.isCargill))
    get isWetOrCargill() {
        return this.isWet || this.isCargill;
    }

    @computedFrom('item.estimatedArrivalDate')
    get estimatedArrivalDate() {
        return this.item ? this.item.estimatedArrivalDate : null;
    }

    set estimatedArrivalDate(value) {
        this.item.estimatedArrivalDate = value;
        this._resettingMaxDate = true;
    }

    @computedFrom('item.estimatedArrivalDate')
    get estimatedArrivalDisabledDates() {
        if (this.item && this.item.estimatedArrivalDate && moment(this.item.estimatedArrivalDate).isBefore(moment(), 'd')) {
            const dates = [];
            let date = moment(this.item.estimatedArrivalDate).add(1, 'd');

            while (moment(date).isBefore(moment(), 'd')) {
                dates.push(date.clone());
                date = date.add(1, 'd');
            }

            return dates;
        }

        return null;
    }

    @computedFrom(Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.isEdit),
    Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.item.isHybrid),
    Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z._isHybrid))
    get isHybrid(): boolean {
        return this.isEdit ? this.item.isHybrid : this._isHybrid;
    }

    @computedFrom(Reflection.getPropertyPath<PortAndOperationalDetailsTab>(z => z.item.requestedDateOfInspection))
    get willBePostponed() {
        if(this.item && this.item.requestedDateOfInspection) {
            return moment(moment(new Date())).add(7, 'd').isAfter(this.item.requestedDateOfInspection, 'd');
        }
        return false;
    }
}